import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ExternalLink } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about-us", label: "About" },
  { to: "/#solutions", label: "Solutions" },
  { to: "/#industries", label: "Industries" },
  { to: "/contact-us", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-line bg-base/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="container-wide flex h-[72px] items-center justify-between gap-6">
        <Link to="/" aria-label="MerchantCore home" data-testid="header-logo-link">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavItem key={item.label} item={item} />
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="https://m.merchantcore.us"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
            data-testid="header-merchant-panel-link"
          >
            MerchantCabinet <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Link to="/apply" className="btn-primary" data-testid="header-cta-get-processing">
            Get started <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-line bg-white/[0.02] text-cloud lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="header-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-line bg-panel/95 backdrop-blur-xl lg:hidden" data-testid="header-mobile-menu">
          <div className="container-wide flex flex-col py-5">
            {NAV.map((item) => (
              <NavItem key={item.label} item={item} mobile />
            ))}
            <a
              href="https://m.merchantcore.us"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 border-b border-line py-4 font-sans text-sm text-emerald"
              data-testid="mobile-merchant-panel-link"
            >
              MerchantCabinet <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Link
              to="/apply"
              className="btn-primary mt-4 w-full"
              data-testid="mobile-cta-get-processing"
            >
              Get started <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ item, mobile = false }) {
  const isHash = item.to.includes("#");
  const base = mobile
    ? "border-b border-line py-4 font-sans text-sm text-cloud/90 hover:text-emerald"
    : "rounded-full px-4 py-2 font-sans text-sm text-muted transition-colors hover:text-cloud";

  if (isHash) {
    return (
      <Link to={item.to} className={base} data-testid={`nav-${item.label.toLowerCase()}`}>
        {item.label}
      </Link>
    );
  }
  return (
    <NavLink
      to={item.to}
      end
      data-testid={`nav-${item.label.toLowerCase()}`}
      className={({ isActive }) => `${base} ${isActive ? "!text-emerald" : ""}`}
    >
      {item.label}
    </NavLink>
  );
}
